/**
 * Sheet — bottom sheet / modal overlay.
 *
 * Slides up from the bottom over a dimmed backdrop; used for variant pickers,
 * confirmations, requisites, and proof upload. Closes on backdrop tap or Escape.
 * Renders nothing when ``open`` is ``false``.
 */
import { useEffect } from "react";
import type { ReactNode } from "react";

import { cn } from "../utils/cn";
import styles from "./Sheet.module.css";

export interface SheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Optional heading rendered in the sticky header. */
  readonly title?: ReactNode;
  /** Hide the drag handle / dismiss affordances (e.g. blocking flows). */
  readonly dismissible?: boolean;
  readonly children?: ReactNode;
  readonly className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  dismissible = true,
  children,
  className,
}: SheetProps): JSX.Element | null {
  useEffect(() => {
    if (!open || !dismissible) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [open, dismissible, onClose]);

  // Lock background scroll while the sheet is open so wheel/touch gestures don't
  // "fall through" to the page behind the overlay. Restored on close/unmount.
  useEffect(() => {
    if (!open || typeof document === "undefined") {
      return;
    }
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousTouch = body.style.touchAction;
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    return () => {
      body.style.overflow = previousOverflow;
      body.style.touchAction = previousTouch;
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className={styles.overlay}
      onClick={dismissible ? onClose : undefined}
      role="presentation"
    >
      <div
        className={cn(styles.sheet, className)}
        role="dialog"
        aria-modal="true"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {dismissible ? <div className={styles.handle} aria-hidden="true" /> : null}
        {title !== undefined ? (
          <header className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            {dismissible ? (
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Закрыть"
              >
                ✕
              </button>
            ) : null}
          </header>
        ) : null}
        <div className={styles.body}>{children}</div>
      </div>
    </div>
  );
}
